import React, { ReactNode, createContext, useContext, useMemo } from 'react'

import useLocalStorage from 'modules/core/lib/useLocalStorage'

// Add new feature flags to this array
const CLIENT_SIDE_FEATURE_FLAGS = ['dark-mode'] as const

export type ClientSideFeatureFlag = (typeof CLIENT_SIDE_FEATURE_FLAGS)[number]

type FeatureFlagState = Record<string, boolean>

interface ClientSideFeatureFlagContextType {
  isFeatureFlagEnabled: (flag: ClientSideFeatureFlag) => boolean
  toggleFeatureFlag: (flag: ClientSideFeatureFlag) => void
  enableFeatureFlag: (flag: ClientSideFeatureFlag) => void
  disableFeatureFlag: (flag: ClientSideFeatureFlag) => void
  featureFlags: Array<ClientSideFeatureFlag>
}

const CLIENT_SIDE_FEATURE_FLAGS_STORAGE_KEY = 'factorialClientSideFeatureFlags'

const ClientSideFeatureFlagContext = createContext<ClientSideFeatureFlagContextType | undefined>(
  undefined
)

interface ClientSideFeatureFlagProviderProps {
  children: ReactNode
}

export const ClientSideFeatureFlagProvider: React.FC<ClientSideFeatureFlagProviderProps> = ({
  children,
}) => {
  const [featureFlags, setFeatureFlags] = useLocalStorage<FeatureFlagState>(
    CLIENT_SIDE_FEATURE_FLAGS_STORAGE_KEY,
    {}
  )

  const contextValue = useMemo(() => {
    const currentFlags = featureFlags || {}

    return {
      isFeatureFlagEnabled: (flag: ClientSideFeatureFlag): boolean => {
        return Boolean(currentFlags[flag])
      },

      toggleFeatureFlag: (flag: ClientSideFeatureFlag): void => {
        setFeatureFlags({
          ...currentFlags,
          [flag]: !currentFlags[flag],
        })
      },

      enableFeatureFlag: (flag: ClientSideFeatureFlag): void => {
        setFeatureFlags({
          ...currentFlags,
          [flag]: true,
        })
      },

      disableFeatureFlag: (flag: ClientSideFeatureFlag): void => {
        setFeatureFlags({
          ...currentFlags,
          [flag]: false,
        })
      },

      featureFlags: [...CLIENT_SIDE_FEATURE_FLAGS],
    }
  }, [featureFlags, setFeatureFlags])

  return (
    <ClientSideFeatureFlagContext.Provider value={contextValue}>
      {children}
    </ClientSideFeatureFlagContext.Provider>
  )
}

export const useClientSideFeatureFlag = (): ClientSideFeatureFlagContextType => {
  const context = useContext(ClientSideFeatureFlagContext)

  if (context === undefined) {
    throw new Error('useClientSideFeatureFlag must be used within a ClientSideFeatureFlagProvider')
  }

  return context
}
